import { useState } from 'react';
import { EquipmentItem } from '@/backend/models/equipmentItem';
import { EquipmentConnectionState } from '@/frontend/hooks/useEquipmentConnection';
import { DESKTOP_INSTRUMENT_STATE, DesktopInstrument } from '@/types/instrumentBridgeTypes';
import { ConnectionConfigModal } from './ConnectionConfigModal';
import { InstrumentPicker } from './InstrumentPicker';
import { InstrumentStatus } from '../instruments/InstrumentStatus';
import { RunningStateButton } from '../instruments/RunningStateButton';

/**
 * The instrument picker, its start/stop button and its state badge.
 *
 * Shared by the card on an equipment operation and the cell in the equipment
 * table, so the two cannot drift. The picker, button and badge are all
 * veriform-desktop's, so an instrument reads the same wherever it is shown.
 *
 * Choosing an instrument opens the connect dialog rather than taking effect, as
 * it does there: nothing is recorded until that dialog is confirmed, so opening
 * the list and closing it again changes nothing.
 */

/**
 * After `getButtonState` (veriform-desktop, CurrentInstrumentTable.ts:102),
 * narrowed to what this page can know. Desktop reads the provider's live state
 * and the connection's; here there is only the error from the last connect or
 * disconnect, and whether `listConnections` reports a connection.
 *
 * The instrument's own `providerState` is not read: it is a snapshot from when
 * the page loaded, so an instrument errored then would read as errored after
 * every later successful connect.
 *
 * An error outranks being connected, so a provider that failed to start shows
 * as errored rather than running.
 */
function buttonState(connection: EquipmentConnectionState): DESKTOP_INSTRUMENT_STATE {
  if (connection.error) return DESKTOP_INSTRUMENT_STATE.ERROR;
  if (connection.ready) return DESKTOP_INSTRUMENT_STATE.RUNNING;
  return DESKTOP_INSTRUMENT_STATE.STOPPED;
}

export function InstrumentSelect(props: {
  equipment: EquipmentItem;
  connection: EquipmentConnectionState;
}) {
  const { equipment, connection } = props;
  // The instrument being configured, which is not the assigned one until the
  // dialog is confirmed.
  const [choosing, setChoosing] = useState<DesktopInstrument>();
  const state = buttonState(connection);

  return (
    <div className="d-flex align-items-center gap-2">
      <InstrumentPicker
        instruments={connection.instruments}
        selected={connection.assigned}
        // Changing it under a live connection would leave the engine holding
        // one for an instrument the page no longer thinks is assigned.
        disabled={connection.connected || connection.busy}
        onSelect={setChoosing}
      />

      { choosing && (
        <ConnectionConfigModal
          equipment={equipment}
          connection={connection}
          instrument={choosing}
          onClose={() => setChoosing(undefined)}
        />
      )}

      { connection.connectable && (
        <RunningStateButton
          state={state}
          pending={connection.busy}
          disabled={!connection.canToggle}
          handleStart={connection.start}
          handleStop={connection.stop}
        />
      )}

      <InstrumentStatus status={state} error={connection.error} />
    </div>
  );
}
